import axios from 'axios';
import { apiConfig } from '../config/api';

export const API_BASE_URL = apiConfig.baseURL;

// Hardcoded image base URL
export const IMAGE_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

const normalizeImagePath = (imagePath) => {
  if (!imagePath) return '';

  let normalized = String(imagePath).trim();

  normalized = normalized.replace(/^https?:\/(?!\/)/, (m) => m.replace(':/', '://'));

  if (normalized.startsWith('/https://')) normalized = normalized.slice(1);
  if (normalized.startsWith('/http://')) normalized = normalized.slice(1);

  if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
    try {
      const url = new URL(normalized);
      if (url.pathname.startsWith('/wwwroot/')) url.pathname = url.pathname.replace('/wwwroot', '');
      if (url.pathname.startsWith('/api/')) url.pathname = url.pathname.replace('/api', '');
      return url.toString();
    } catch {
      return normalized;
    }
  }

  if (normalized.startsWith('/wwwroot/')) normalized = normalized.replace('/wwwroot', '');
  if (normalized.startsWith('wwwroot/')) normalized = normalized.replace('wwwroot', '');

  if (normalized.startsWith('/api/')) normalized = normalized.replace('/api', '');
  if (normalized.startsWith('api/')) normalized = normalized.replace('api', '');

  return normalized;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Hotels API
export const getHotels = async () => {
  try {
    const response = await api.get('/CustomerWebsite/hotels');
    return response.data;
  } catch (error) {
    console.error('Error fetching hotels:', error);
    throw error;
  }
};

// Room Types API
export const getRoomTypes = async () => {
  try {
    const response = await api.get('/CustomerWebsite/room-types');
    return response.data;
  } catch (error) {
    console.error('Error fetching room types:', error);
    throw error;
  }
};

// Room Types API (RoomsManagement) - used by Reservation page for admin-driven room types
export const getRoomTypesRoomsManagement = async () => {
  try {
    const response = await api.get('/RoomsManagement/room-types');
    return response.data;
  } catch (error) {
    console.error('Error fetching room types (RoomsManagement):', error);
    throw error;
  }
};

// Room Rates API - Get pricing and rate details
export const getRoomRates = async () => {
  try {
    const response = await api.get('/RoomsManagement/room-rates');
    return response.data;
  } catch (error) {
    console.error('Error fetching room rates:', error);
    throw error;
  }
};

// Get Room Rates by Room Type
export const getRoomRatesByType = async (roomTypeName) => {
  try {
    const response = await api.get(`/RoomsManagement/room-rates?roomType=${roomTypeName}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching room rates by type:', error);
    throw error;
  }
};

// Room Amenities API - filter by room type
export const getRoomAmenitiesByRoomTypeId = async (roomTypeId) => {
  try {
    const response = await api.get(`/RoomsManagement/room-amenities?roomTypeId=${roomTypeId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching room amenities:', error);
    throw error;
  }
};

// Room Tax API - filter by room type
export const getRoomTaxesByRoomTypeId = async (roomTypeId) => {
  try {
    const response = await api.get(`/RoomTax/roomtype/${roomTypeId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching room taxes:', error);
    throw error;
  }
};

// Search Rooms API
export const searchRooms = async (searchParams) => {
  try {
    const response = await api.post('/CustomerWebsite/search-rooms', searchParams);
    return response.data;
  } catch (error) {
    console.error('Error searching rooms:', error);
    throw error;
  }
};

// Get Room Type Detail
export const getRoomTypeDetail = async (id) => {
  try {
    const response = await api.get(`/CustomerWebsite/room-type/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching room type detail:', error);
    throw error;
  }
};

// Check Availability
export const checkAvailability = async (availabilityParams) => {
  try {
    const response = await api.post('/CustomerWebsite/check-availability', availabilityParams);
    return response.data;
  } catch (error) {
    console.error('Error checking availability:', error);
    throw error;
  }
};

// Rate Plans (Policies) API
export const getApplicablePlans = async ({ hotelId, roomTypeId }) => {
  try {
    const response = await api.get(`/Plans/applicable?hotelId=${hotelId}&roomTypeId=${roomTypeId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching applicable plans:', error);
    throw error;
  }
};

// Property Module API
export const getPropertyCategories = async () => {
  try {
    const response = await api.get('/PropertyListings/categories');
    return response.data;
  } catch (error) {
    console.error('Error fetching property categories:', error);
    throw error;
  }
};

export const getPropertyTypes = async () => {
  try {
    const response = await api.get('/PropertyListings/types');
    return response.data;
  } catch (error) {
    console.error('Error fetching property types:', error);
    throw error;
  }
};

export const getPropertyLocations = async () => {
  try {
    const response = await api.get('/PropertyListings/locations');
    return response.data;
  } catch (error) {
    console.error('Error fetching property locations:', error);
    throw error;
  }
};

export const getPropertyListings = async (params = {}) => {
  try {
    const response = await api.get('/PropertyListings', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching property listings:', error);
    throw error;
  }
};

export const getPropertyListingById = async (id) => {
  try {
    const response = await api.get(`/PropertyListings/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching property listing details:', error);
    throw error;
  }
};

export const createPropertyBooking = async (bookingData) => {
  try {
    const response = await api.post('/PropertyBookings', bookingData);
    return response.data;
  } catch (error) {
    console.error('Error creating property booking:', error);
    throw error;
  }
};

export const getPropertyBooking = async (id) => {
  try {
    const response = await api.get(`/PropertyBookings/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching property booking:', error);
    throw error;
  }
};

export const initiatePropertyPayment = async (paymentData) => {
  try {
    const response = await api.post('/PropertyBookings/initiate-payment', paymentData);
    return response.data;
  } catch (error) {
    console.error('Error initiating property payment:', error);
    throw error;
  }
};

// Create Reservation
export const createReservation = async (reservationData) => {
  try {
    const response = await api.post('/CustomerReservation/create', reservationData);
    return response.data;
  } catch (error) {
    console.error('Error creating reservation:', error);
    throw error;
  }
};

// Get Reservation by Number
export const getReservation = async (reservationNumber) => {
  try {
    const response = await api.get(`/CustomerReservation/${reservationNumber}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching reservation:', error);
    throw error;
  }
};

// Helper function to get full image URL
export const getImageUrl = (imagePath, folder = '') => {
  if (!imagePath) {
    if (folder === 'logos') return `${IMAGE_BASE_URL}/uploads/logos/JCP.png`;
    if (folder === 'restaurant') return `${IMAGE_BASE_URL}/Gallery/Restaurants/restaurant-islamabad.jpg`;
    return `${IMAGE_BASE_URL}/uploads/default-image.jpg`;
  }

  let normalized = normalizeImagePath(imagePath);

  if (normalized.startsWith('http://') || normalized.startsWith('https://')) {
    return normalized;
  }

  if (normalized.startsWith('/')) {
    const fullUrl = `${IMAGE_BASE_URL}${normalized}`;
    console.log('[getImageUrl] Absolute path', { imagePath, normalized, IMAGE_BASE_URL, fullUrl });
    return fullUrl;
  }
  
  // If folder is provided, construct path with folder
  if (folder) {
    const fullUrl = `${IMAGE_BASE_URL}/uploads/${folder}/${normalized.replace(/^\/+/, '')}`;
    console.log('[getImageUrl] Using folder', { imagePath, normalized, folder, IMAGE_BASE_URL, fullUrl });
    return fullUrl;
  }
  
  // Default: treat as relative path
  const fullUrl = `${IMAGE_BASE_URL}/${normalized.replace(/^\/+/, '')}`;
  console.log('[getImageUrl] Relative path', { imagePath, normalized, IMAGE_BASE_URL, fullUrl });
  return fullUrl;
};

export const formatGBP = (amount = 0) => {
  const numericAmount = Number(amount || 0);
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(numericAmount);
};

export default api;
